import type { ReactNode } from "react";

import { Label } from "@/components/ui/label";
import type { CampoConAsistencia as CodigoDeCampoConAsistencia } from "@/lib/ia";
import { cn } from "@/lib/utils";

import { CampoConAsistencia } from "./CampoConAsistencia";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { PanelDeTerrenoDeLaDescripcion } from "./PanelDeTerrenoDeLaDescripcion";
import { PildoraDeValidacion } from "./PildoraDeValidacion";
import { RedactarDesdeTerreno } from "./RedactarDesdeTerreno";
import { campoDeTerrenoDeSuelos, type CampoDeSuelosEnTerreno } from "./cuadroDeSuelos";
import {
  puedeRedactarDesdeTerreno,
  vistaDeTerreno,
  type EstadoDelTerreno,
} from "./terrenoDeAntecedentes";

interface DescripcionDeSuelosProps {
  informeId: number;
  /** "01" o "02": el cuadradito oscuro del encabezado. */
  numero: string;
  idDelTitulo: string;
  titulo: string;
  bajada: string;
  /** El id del `<textarea>`. */
  idDelCampo: string;
  /** El nombre accesible del `<textarea>`: el rótulo visible es el título. */
  etiqueta: string;
  /** El nombre accesible de la casilla: distingue una de la otra. */
  etiquetaDeLaValidacion: string;
  placeholder: string;
  /** El campo con asistencia de redacción ("Mejorar redacción"). */
  campoConAsistencia: CodigoDeCampoConAsistencia;
  /** De cuál de los dos campos de terreno sale el panel y el borrador. */
  campoDeTerreno: CampoDeSuelosEnTerreno;
  value: string;
  onChange: (texto: string) => void;
  validada: boolean;
  onValidada: (validada: boolean) => void;
  disabled: boolean;
  /** La fuente que declaró la IA para el texto, mientras siga siendo el propuesto. */
  fuente?: ReactNode;
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita. */
  asignado: string | null;
  onVerTerreno?: () => void;
  onAbrirFoto: (uuid: string) => void;
  /** Recibe el texto que redactó el servidor desde terreno, para quitar su fuente y desmarcar la casilla. */
  onRedactado: (texto: string) => void;
}

/** Los botones de la fila del diseño: 11,5 px, radio 10 y más bajos que `sm`. */
const CLASE_DEL_BOTON =
  "h-8 rounded-[0.625rem] px-3 text-[0.71875rem] has-[>svg]:px-3 [&_svg:not([class*='size-'])]:size-3.5";

/**
 * Las tarjetas 01 (descripción general de los suelos) y 02 (topografía): un
 * texto con lo que el encargado grabó en terreno al lado.
 *
 * SON LA MISMA TARJETA DOS VECES, y por eso un solo componente: lo único que
 * cambia es el campo de terreno de donde sale el panel, el campo con asistencia
 * y los rótulos. Con dos copias, la regla de "Redactar desde terreno" se
 * corregiría en una y no en la otra.
 *
 * "REDACTAR DESDE TERRENO" PROPONE Y NO GUARDA: el texto entra al campo como
 * cambio sin guardar y la casilla "Información validada" se desmarca — lo
 * escribió un modelo y una persona tiene que leerlo. El botón solo se ofrece
 * cuando el campo trae algo que leer (un texto o una transcripción lista): en
 * los demás casos el servidor respondería 422.
 */
export function DescripcionDeSuelos({
  informeId,
  numero,
  idDelTitulo,
  titulo,
  bajada,
  idDelCampo,
  etiqueta,
  etiquetaDeLaValidacion,
  placeholder,
  campoConAsistencia,
  campoDeTerreno,
  value,
  onChange,
  validada,
  onValidada,
  disabled,
  fuente,
  terreno,
  asignado,
  onVerTerreno,
  onAbrirFoto,
  onRedactado,
}: DescripcionDeSuelosProps) {
  const terrenoActivo = terreno && terreno.estado !== "no_disponible" ? terreno : undefined;
  const registro = terrenoActivo?.estado === "ok" ? terrenoActivo.registro : undefined;
  const campo = campoDeTerrenoDeSuelos(registro, campoDeTerreno);

  const redactar =
    terrenoActivo?.estado === "ok" && puedeRedactarDesdeTerreno(campo) ? (
      <RedactarDesdeTerreno
        informeId={informeId}
        campo={campoDeTerreno}
        valorActual={value}
        claseDelBoton={CLASE_DEL_BOTON}
        onRedactado={onRedactado}
      />
    ) : null;

  const pildora = (
    <PildoraDeValidacion
      validada={validada}
      etiqueta={etiquetaDeLaValidacion}
      disabled={disabled}
      onValidada={onValidada}
    />
  );

  return (
    <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
      <EncabezadoNumerado
        numero={numero}
        idDelTitulo={idDelTitulo}
        titulo={titulo}
        bajada={bajada}
      />

      <div
        className={cn(
          "mt-3.5 grid items-stretch gap-4",
          terrenoActivo && "lg:grid-cols-[minmax(0,1fr)_minmax(0,23.75rem)]",
        )}
      >
        <div className="flex min-w-0 flex-col gap-2">
          <Label htmlFor={idDelCampo} className="sr-only">
            {etiqueta}
          </Label>
          <CampoConAsistencia
            id={idDelCampo}
            campo={campoConAsistencia}
            value={value}
            onChange={onChange}
            informeId={informeId}
            rows={6}
            llenarAlto
            variantDeMejora="outline"
            claseDelBotonDeMejora={CLASE_DEL_BOTON}
            placeholder={placeholder}
            disabled={disabled}
            accionesAntes={redactar}
            accionesDespues={pildora}
          />
          {fuente}
        </div>

        {terrenoActivo ? (
          <PanelDeTerrenoDeLaDescripcion
            informeId={informeId}
            terreno={terrenoActivo}
            vista={registro ? vistaDeTerreno(registro) : null}
            campo={campo}
            asignado={asignado}
            onVerTodo={onVerTerreno}
            onAbrirFoto={onAbrirFoto}
          />
        ) : null}
      </div>
    </section>
  );
}
